/**
 * tool-progress — one legible progress panel for long in-browser runs.
 *
 * Origin (2026-08-09): a /video-volume user on Yandex/Android re-mixed a 47 MB
 * video. The run took 101 s and worked, but the only feedback was a
 * rgba(255,255,255,.15) fill inside the primary button — measured contrast to
 * the unfilled half: 1.17:1 (light) / 1.24:1 (dark), against WCAG's 3:1 floor
 * for non-text indicators. He pressed Cancel three times, each press restarting
 * the clock, and reported the tool as frozen. FFmpeg had been emitting a tick
 * every ~0.4 s the whole time; nobody could see them.
 *
 * So this module's contract is: while a run is alive the panel must always show
 * something that CHANGED since the user last looked — a percent, an elapsed
 * clock, or a moving stripe — even across the stretches where the engine
 * reports nothing (container read, faststart mux pass).
 *
 * Markup comes from resources/views/components/tool-progress.blade.php.
 *
 *   var pr = window.__toolProgress.get('vvProgress');
 *   pr.onCancel(function(){ ... });
 *   pr.start('Обрабатываем звук…');
 *   pr.set(0.42);            // 0..1, monotonic
 *   pr.finishing();          // engine returned, we are muxing/handing over
 *   pr.done(); pr.hide();
 */
(function () {
    'use strict';
    if (window.__toolProgress) return;

    var I18N = window.__TPROG_I18N || {};
    var t = function (k, n) {
        var s = I18N[k] || '';
        return n === undefined ? s : s.replace(':n', n);
    };

    /* Work time, not wall time (window.__activeNow: minus a frozen tab and a
       sleeping device). 09.10.2026, /video-compressor on Android: the browser
       was put in the background 28 s into the encode, the tab froze, and the
       panel's clock read «96:42» at 1 % — the 96 minutes were the phone's, not
       the run's, and the estimate that grows on a quiet engine grew on them too.
       Fallback to the wall clock where the core is absent. */
    var clock = function () { return window.__activeNow ? window.__activeNow() : Date.now(); };

    function mmss(sec) {
        sec = Math.max(0, Math.round(sec));
        var m = Math.floor(sec / 60), s = sec % 60;
        return m + ':' + (s < 10 ? '0' : '') + s;
    }

    /* EtaTracker — the "how much longer" arithmetic, with no DOM attached.
     *
     * Split out of Panel (2026-08-16) because /audio-enhancer needs the answer
     * without adopting this panel's markup: it is the platform's longest audio
     * run (measured 247 s for a 68 MB WAV on a throttled mid Android) and its
     * own progress block is wired through 74 call sites, so a markup migration
     * would be risk with no user-visible gain. The alternative — a private copy
     * of the extrapolation — is exactly the sixteenth-dialect problem this file
     * exists to end, so the maths moves here and stays single-source.
     *
     * Contract: feed it ratios, ask it for seconds. Formatting stays with the
     * caller (Panel uses __TPROG_I18N; a tool may use its own strings).
     *
     *   var tr = window.__toolProgress.tracker();
     *   tr.reset(); tr.set(0.42); tr.tick();
     *   tr.remainingSec();   // number | null  (null = not confident yet)
     */
    function EtaTracker() { this.reset(); }

    EtaTracker.prototype.reset = function () {
        this.anchor = null; this.ema = 0; this.hi = 0; this._rem = null;
        return this;
    };

    EtaTracker.prototype.set = function (ratio) {
        var r = +ratio;
        if (!isFinite(r) || r < 0) r = 0;
        if (r > 1) r = 1;
        if (r < this.hi) r = this.hi;      // monotonic: a bar that walks back reads as a glitch
        this.hi = r;
        var p = r * 100;
        // Anchor late enough that startup cost (WASM init, decode) is not
        // extrapolated across the whole run.
        if (p >= 6 && !this.anchor) this.anchor = { t: clock(), p: p };
        var a = this.anchor;
        // Below the anchor, and until the run has travelled far enough past it,
        // any number would be extrapolated from noise. Say nothing instead.
        if (!a || p - a.p < 4 || p >= 99) { this._rem = null; return this; }
        var raw = ((clock() - a.t) / 1000) * (100 - p) / (p - a.p);
        this.ema = this.ema ? this.ema * 0.7 + raw * 0.3 : raw;
        this._rem = this.ema;
        return this;
    };

    /* Re-extrapolate at the current ratio. The clock is the point: when the
     * engine goes quiet the estimate must GROW, not freeze at its last value. */
    EtaTracker.prototype.tick = function () { return this.set(this.hi); };

    EtaTracker.prototype.remainingSec = function () { return this._rem; };

    /* Rounded, human-facing seconds — the same buckets Panel renders, so a tool
     * formatting its own string lands on identical numbers. */
    EtaTracker.prototype.roundedSec = function () {
        var rem = this._rem;
        if (rem == null) return null;
        if (rem >= 90) return Math.max(60, Math.round(rem / 60) * 60);
        if (rem >= 10) return Math.max(5, Math.round(rem / 5) * 5);
        return null;
    };

    function Panel(root) {
        this.root = root;
        this.eta = new EtaTracker();
        this.elLabel = root.querySelector('[data-tprog-label]');
        this.elPct = root.querySelector('[data-tprog-pct]');
        this.elTrack = root.querySelector('[data-tprog-track]');
        this.elFill = root.querySelector('[data-tprog-fill]');
        this.elEta = root.querySelector('[data-tprog-eta]');
        this.elElapsed = root.querySelector('[data-tprog-elapsed]');
        this.elCancel = root.querySelector('[data-tprog-cancel]');
        this.t0 = 0; this.hi = 0; this.anchor = null; this.ema = 0; this.timer = 0;
        this.gen = 0; this._finishing = false;
    }

    Panel.prototype.onCancel = function (fn) {
        if (!this.elCancel) return this;
        if (!this._cancelBound) {
            this._cancelBound = true;
            var self = this;
            this.elCancel.addEventListener('click', function () {
                if (self._cancelFn) self._cancelFn();
            });
        }
        this._cancelFn = fn;
        this.elCancel.hidden = !fn;
        return this;
    };

    /* opts.indeterminate — a phase with no honest ratio (audio decode, loudness
       analysis). Prod median for that phase on /video-volume is 24.6 s and the
       tail reaches 271 s, all of it previously shown as a motionless "…". */
    /* Every start() takes ownership. A cancelled run keeps unwinding after the
       user has already launched the next one, and its finally-block would
       otherwise hide the panel the NEW run is drawing into — the same invisible
       progress, reached by a different door. Callers hold the gen they started
       with and skip their teardown when it is stale. */
    Panel.prototype.start = function (label, opts) {
        opts = opts || {};
        this.gen = (this.gen || 0) + 1;
        this.t0 = clock(); this.hi = 0; this._finishing = false;
        this.eta.reset();
        this._indet = !!opts.indeterminate;
        this.root.classList.toggle('tprog-indet', this._indet);
        this.root.hidden = false;
        if (this.elCancel && this._cancelFn) this.elCancel.hidden = false;
        this.label(label || '');
        this._paint(0);
        this._tick();
        clearInterval(this.timer);
        var self = this;
        // 1 s heartbeat: the elapsed clock is the one thing that keeps moving
        // when the engine goes quiet, so it must not depend on progress events.
        this.timer = setInterval(function () { self._tick(); }, 1000);
        return this;
    };

    Panel.prototype.label = function (text) {
        if (this.elLabel && text != null) this.elLabel.textContent = text;
        return this;
    };

    Panel.prototype.set = function (ratio) {
        if (this._finishing) return this;
        // A real ratio arrived — leave indeterminate mode rather than keep sweeping.
        if (this._indet) { this._indet = false; this.root.classList.remove('tprog-indet'); }
        var r = +ratio;
        if (!isFinite(r) || r < 0) r = 0;
        if (r > 1) r = 1;
        if (r < this.hi) r = this.hi;      // a bar that walks backwards reads as a glitch
        this.hi = r;
        this.eta.set(r);
        this._paint(r);
        this._tick();
        return this;
    };

    /* The engine has returned but the file is not on screen yet (readFile, Blob,
       player attach). Left alone the bar would sit at its last percent for those
       seconds — the exact "it froze at 95 %" complaint. Say what is happening. */
    Panel.prototype.finishing = function (label) {
        this._finishing = true;
        this._indet = false; this.root.classList.remove('tprog-indet');
        this.label(label || t('finishing'));
        this._paint(Math.max(this.hi, 0.97));
        if (this.elEta) this.elEta.textContent = '';
        return this;
    };

    Panel.prototype.done = function () {
        this._finishing = true;
        this._indet = false; this.root.classList.remove('tprog-indet');
        this._paint(1);
        clearInterval(this.timer); this.timer = 0;
        if (this.elCancel) this.elCancel.hidden = true;
        if (this.elEta) this.elEta.textContent = '';
        return this;
    };

    Panel.prototype.hide = function () {
        clearInterval(this.timer); this.timer = 0;
        this.root.hidden = true;
        this._finishing = false;
        return this;
    };

    Panel.prototype._paint = function (r) {
        var pct = Math.round(r * 100);
        if (this.elFill) {
            /* In indeterminate mode the width belongs to the stylesheet — the
               sweep animates a fixed-width bar across the track. An inline
               width wins over that rule and pins the bar to 0, i.e. an empty
               track that looks exactly like the frozen UI this file replaces.
               Caught only by screenshotting the state, never by the DOM. */
            if (this._indet) this.elFill.style.removeProperty('width');
            else this.elFill.style.width = pct + '%';
        }
        if (this.elPct) this.elPct.textContent = pct + '%';
        if (this.elTrack) this.elTrack.setAttribute('aria-valuenow', String(pct));
    };

    Panel.prototype._tick = function () {
        if (!this.t0) return;
        var elapsed = (clock() - this.t0) / 1000;
        if (this.elElapsed) this.elElapsed.textContent = mmss(elapsed);
        if (this._finishing || !this.elEta) return;

        // No ratio to extrapolate from in indeterminate mode — say only the
        // part that is true, and let the clock carry the "alive" signal.
        if (this._indet) { this.elEta.innerHTML = esc(t('keep_tab')); return; }

        var keep = t('keep_tab');
        // Re-extrapolate against the work clock: a stalled engine must make the
        // estimate grow rather than freeze at its last value (a frozen tab is not a stall).
        var sec = this.eta.tick().roundedSec();
        var txt = '';
        if (sec != null) txt = sec >= 60 ? t('eta_min', Math.round(sec / 60)) : t('eta_sec', sec);
        this.elEta.innerHTML = (txt ? '<b>' + esc(txt) + '</b> · ' : '') + esc(keep);
    };

    function esc(s) {
        return String(s == null ? '' : s)
            .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }

    /* ── floating() — a panel for a page that never declared one ──────────
     *
     * Why (2026-08-31, /extend-song «Почему музыка не скачивается?»): the
     * long-run panel above is opt-in markup, so it only exists where somebody
     * remembered to add it. The operation it was built for — a browser encode
     * that runs for minutes — is reached from 35 call sites of
     * AudioPipeline.encodeMP3, and 34 of them pass no onProgress at all. The
     * worker has been emitting a percent the whole time; nothing was listening.
     *
     * So the encoder needs a panel it can raise itself, on a page whose author
     * never thought about it. That panel cannot depend on the component's CSS
     * (the page did not include it), so the markup is built here with inline
     * styles and the SAME data-tprog-* hooks — the arithmetic, the ETA, the
     * elapsed clock and the cancel control stay in Panel, single-source. Only
     * the skin differs.
     *
     * It sits fixed above the consent banner, like __dlHold's bar: the caller
     * has no idea where on the page the user is looking, and on /extend-song
     * the tool's own status line sat 555 px above the button that started the
     * run — legible in the DOM, invisible to the person.
     */
    function floatingRoot() {
        var d = document.createElement('div');
        d.className = 'tprog tprog-float';
        d.setAttribute('role', 'region');
        d.style.cssText = 'position:fixed;left:50%;transform:translateX(-50%);bottom:12px;z-index:9992;' +
            'width:min(460px,94vw);box-sizing:border-box;display:flex;flex-direction:column;gap:8px;' +
            'background:rgba(20,24,32,.96);color:#fff;border:1px solid rgba(255,255,255,.18);' +
            'border-radius:14px;padding:12px 14px;font:13px/1.4 system-ui,sans-serif;' +
            'box-shadow:0 6px 22px rgba(0,0,0,.4)';
        d.innerHTML =
            '<div style="display:flex;align-items:baseline;gap:10px">' +
              '<span data-tprog-label style="flex:1;min-width:0;font-weight:600"></span>' +
              '<span data-tprog-pct style="font-weight:800;font-variant-numeric:tabular-nums;color:#60a5fa">0%</span>' +
            '</div>' +
            '<div data-tprog-track role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="0" ' +
                 'style="height:10px;border-radius:999px;overflow:hidden;background:rgba(255,255,255,.12);border:1px solid rgba(255,255,255,.18)">' +
              '<div data-tprog-fill style="height:100%;width:0;border-radius:999px;background:#3b82f6;transition:width .3s linear"></div>' +
            '</div>' +
            '<div style="display:flex;align-items:flex-start;gap:10px;flex-wrap:wrap;font-size:12px;color:rgba(255,255,255,.75)">' +
              '<span data-tprog-eta style="flex:1;min-width:0"></span>' +
              '<span data-tprog-elapsed style="font-variant-numeric:tabular-nums;opacity:.7"></span>' +
            '</div>' +
            '<button type="button" data-tprog-cancel hidden ' +
                'style="align-self:flex-start;background:none;border:1px solid rgba(255,255,255,.3);color:rgba(255,255,255,.75);' +
                'font-family:inherit;font-size:12px;padding:5px 12px;border-radius:999px;cursor:pointer"></button>';
        var c = d.querySelector('[data-tprog-cancel]');
        try { c.textContent = t('cancel') || 'Cancel'; } catch (_) {}
        return d;
    }

    /* The 152-ФЗ consent banner is fixed to bottom:0 — sitting under it would
       hide the very thing this panel exists to show. Same arithmetic as
       __dlHold.place(). */
    function ccHeight() {
        try {
            var b = document.querySelector('.cc-banner');
            if (!b || !document.body.contains(b)) return 0;
            var st = window.getComputedStyle(b);
            if (st.display === 'none' || st.visibility === 'hidden') return 0;
            return Math.round(b.getBoundingClientRect().height) || 0;
        } catch (_) { return 0; }
    }

    var cache = {};
    window.__toolProgress = {
        get: function (id) {
            if (cache[id]) return cache[id];
            var root = document.getElementById(id);
            if (!root) return null;
            return (cache[id] = new Panel(root));
        },
        /* DOM-free estimator for tools that keep their own progress markup.
           Same arithmetic the panel runs — see EtaTracker. */
        tracker: function () { return new EtaTracker(); },
        /* A page-owned panel is always preferred; this is for code that runs on
           pages it does not own. hide() detaches, so nothing is left behind. */
        floating: function () {
            var root = floatingRoot();
            document.body.appendChild(root);
            root.style.bottom = (12 + ccHeight()) + 'px';
            var p = new Panel(root);
            var origHide = p.hide.bind(p);
            p.hide = function () { origHide(); try { root.remove(); } catch (_) {} return p; };
            return p;
        },
        /* True when some panel is already telling the user a run is alive. A
           second one would be two answers to one question. */
        anyVisible: function () {
            try { return !!document.querySelector('.tprog:not([hidden])'); } catch (_) { return false; }
        }
    };
})();
